import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { z } from 'zod'

import * as atypes from 'common/settings/SettingsReduxActionTypes'
import { DevLogsApi } from 'shared/api/requests/devLogs'
import {
  DevLogItem,
  DevLogsApiSchemas,
  DevLogTotalCounts,
} from 'shared/api/requests/devLogs/schemas'
import { createAsyncAction } from 'shared/lib/redux/createAsyncAction'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

type FetchDevLogsResponse = z.infer<typeof DevLogsApiSchemas.fetchList.response>
type MarkDevLogsAsReadResponse = z.infer<typeof DevLogsApiSchemas.markAsRead.response>
const errorMarkupOptions = { scope: 'settings', section: 'dev_logs' } as const

export interface DevLogsData {
  items: DevLogItem[]
  limiter: number | null
  total: DevLogTotalCounts
}

export interface DevLogsState {
  data: DevLogsData | null
  fetching: boolean
  requestId: string | null
}

const hasNoDataAndIsIdle = ({ data, fetching }: DevLogsState) => data === null && !fetching
const canFetchNextPage = ({ data, fetching }: DevLogsState) =>
  Boolean(data && !fetching && data.limiter !== null)
const isStaleRequest = (state: DevLogsState, requestId: string) => state.requestId !== requestId
const startRequest = (state: DevLogsState, requestId: string) => {
  state.fetching = true
  state.requestId = requestId
}
const finishRequest = (state: DevLogsState) => {
  state.fetching = false
  state.requestId = null
}

export const fetchDevLogs = createAsyncAction<FetchDevLogsResponse>(
  'devLogs/fetchDevLogs',
  async () => {
    const response = await DevLogsApi.fetchList()
    return response.data
  },
  {
    errorMarkupOptions,
    condition: (_, { getState }) => hasNoDataAndIsIdle(getState().devLogs),
  },
)

export const fetchNextDevLogs = createAsyncAction<FetchDevLogsResponse>(
  'devLogs/fetchNextDevLogs',
  async (_, { getState }) => {
    const limiter = getState().devLogs.data?.limiter
    const response = await DevLogsApi.fetchList({ query: { limiter } })
    return response.data as FetchDevLogsResponse
  },
  {
    errorMarkupOptions,
    condition: (_, { getState }) => canFetchNextPage(getState().devLogs),
  },
)

export const markDevLogsAsRead = createAsyncAction<MarkDevLogsAsReadResponse>(
  'devLogs/markDevLogsAsRead',
  async (_, { dispatch }) => {
    const response = await DevLogsApi.markAsRead()
    dispatch({ type: atypes.SETTINGS_DEVLOGS_MARK_AS_READ_RESPONSE, data: response.data })

    return response.data
  },
  { errorMarkupOptions },
)

const initialState: DevLogsState = {
  data: null,
  fetching: false,
  requestId: null,
}

const getItemKey = (item: DevLogItem) => item.trace_id

const devLogsSlice = createSlice({
  name: 'devLogs',
  initialState,
  reducers: {
    dropLogsList() {
      return initialState
    },
    addCreatedNotification(state, action: PayloadAction<DevLogItem>) {
      if (!state.data) {
        return
      }

      const item = action.payload
      if (state.data.items.some((stateItem) => getItemKey(stateItem) === getItemKey(item))) {
        return
      }

      state.data.items.unshift(item)
    },
  },
  extraReducers: (builder) => {
    builder.addCase('APP_UPDATE_CURRENT_ACCOUNT_ID', () => initialState)
    builder.addCase(fetchDevLogs.pending, (state, action) => {
      startRequest(state, action.meta.requestId)
    })
    builder.addCase(fetchDevLogs.fulfilled, (state, action) => {
      if (isStaleRequest(state, action.meta.requestId)) {
        return
      }
      const { list, limiter, total } = action.payload
      state.data = { items: list, limiter, total }
      finishRequest(state)
    })
    builder.addCase(fetchDevLogs.rejected, (state, action) => {
      if (isStaleRequest(state, action.meta.requestId)) {
        return
      }
      finishRequest(state)
    })
    builder.addCase(fetchNextDevLogs.pending, (state, action) => {
      startRequest(state, action.meta.requestId)
    })
    builder.addCase(fetchNextDevLogs.fulfilled, (state, action) => {
      if (isStaleRequest(state, action.meta.requestId)) {
        return
      }
      const { list, limiter, total } = action.payload
      if (state.data) {
        state.data.items = [...state.data.items, ...list]
        state.data.limiter = limiter
        state.data.total = state.data.total ?? total
      }
      finishRequest(state)
    })
    builder.addCase(fetchNextDevLogs.rejected, (state, action) => {
      if (isStaleRequest(state, action.meta.requestId)) {
        return
      }
      finishRequest(state)
    })
  },
})

export const { dropLogsList, addCreatedNotification } = devLogsSlice.actions
export default devLogsSlice.reducer

anotherTabNotificationsListener.on('devlog_critical', (data, dispatch) => {
  dispatch({ type: atypes.SETTINGS_DEVLOGS_UPDATE_LAST_READ, data })
})

anotherTabNotificationsListener.on('devlog_created', (data, dispatch) => {
  dispatch(addCreatedNotification(data.model))
})
